import { useState } from 'preact/hooks';
import Decimal from 'decimal.js';
import { wouldOverContribute } from '../engine/tfsa';
import { localIsoNow } from '../lib/timezone';
import { clampNonNegative } from '../lib/clamp';
import type { TFSAState } from '../engine/types';
import type { LogEntry } from '../lib/state';
import { Modal } from './Modal';
import { ButtonPrimary } from './ButtonPrimary';
import { ButtonSecondary } from './ButtonSecondary';

interface LogEntryModalProps {
  mode: 'contribution' | 'withdrawal';
  tfsa: TFSAState;
  onClose: () => void;
  onSubmit: (entry: Omit<LogEntry, 'id' | 'created_at'>) => void;
  /**
   * v1.0.3 P1: when provided the modal becomes an editor for that entry.
   * Fields prefill, the title/submit label change, and the live over-
   * contribution warning is suppressed (the entry is already counted in
   * tfsa.contributions, so re-warning would double-count and mislead).
   */
  initialEntry?: LogEntry;
}

export function LogEntryModal({ mode, tfsa, onClose, onSubmit, initialEntry }: LogEntryModalProps) {
  const isEditing = !!initialEntry;
  const todayDate = localIsoNow().split('T')[0];
  // Prefill from initialEntry when editing. amount_cents → dollar string
  // (drop trailing ".00" so the input shows "1000" not "1000.00").
  const initialAmountDollars = initialEntry
    ? (() => {
        const dollars = new Decimal(initialEntry.amount_cents).dividedBy(100);
        return dollars.equals(dollars.toFixed(0)) ? dollars.toFixed(0) : dollars.toFixed(2);
      })()
    : '';
  const [amount, setAmount] = useState(initialAmountDollars);
  const [date, setDate] = useState(initialEntry?.date ?? todayDate);
  const [institution, setInstitution] = useState(initialEntry?.institution ?? '');
  // P0-2: Historical-entry toggle. When ON, an over-contribution warning
  // becomes non-blocking so users can document a contribution they
  // already made (e.g. an excess they're now trying to track).
  const [isHistorical, setIsHistorical] = useState(false);
  // P2-5: JS-side date error (HTML max= is bypassable via devtools).
  const [dateError, setDateError] = useState<string | null>(null);

  // v1.0.4 P0: belt-and-suspenders floor at 0. The onInput clamp above
  // catches keystrokes, but a paste-then-no-input or a devtools edit could
  // leak a negative dollar string through. Re-floor before converting to
  // cents so a negative amount can never reach onSubmit / overCheck.
  const amountCents = Decimal.max(
    new Decimal(0),
    new Decimal(amount || '0').times(100)
  ).toFixed(0);

  const overCheck =
    mode === 'contribution' && !isEditing
      ? wouldOverContribute(
          tfsa,
          amountCents,
          isHistorical ? 'historical' : 'prospective'
        )
      : { over: false, by_cents: '0', penalty_pct_per_month: '1.0', severity: 'ok' as const };

  const isBlockedByOverContrib = overCheck.severity === 'error';
  // v1.0.3 P1: in edit mode the entry is already counted in tfsa.contributions,
  // so wouldOverContribute would double-count it. Suppress the live warning;
  // the user is correcting a record, not adding new room consumption.
  const showWarning = mode === 'contribution' && overCheck.over && !isEditing;

  // P2-6: per CRA TFSA rules (@reference/tax-data-2026.md §TFSA
  // "Withdrawal restoration"), excess room recontributes on January 1 of
  // the FOLLOWING calendar year. Compute next_year from the tfsa state's
  // current_date_iso so the message remains correct on Jan-1 rollover.
  const currentYear = parseInt(
    (tfsa.current_date_iso || '').slice(0, 4),
    10
  );
  const nextYear = Number.isFinite(currentYear) ? currentYear + 1 : null;
  const recontribSentence = nextYear
    ? ` You can recontribute the excess on January 1, ${nextYear}.`
    : '';

  function handleSubmit() {
    if (isBlockedByOverContrib) return;
    // P2-5: enforce date <= today server-side; HTML max= is a soft
    // constraint and removable via devtools. Compare ISO 'YYYY-MM-DD'
    // strings (lexicographic === chronological for that format).
    // P2-8: empty string is invalid — falsy short-circuit previously let
    // a blank date through and crashed the dashboard renderer.
    if (!date || (todayDate && date > todayDate)) {
      setDateError(
        date
          ? `Date can't be in the future. Use today (${todayDate}) or earlier.`
          : 'Date is required.'
      );
      return;
    }
    setDateError(null);
    const trimmedInstitution = institution.trim();
    onSubmit({
      type: mode,
      date,
      amount_cents: amountCents,
      ...(trimmedInstitution ? { institution: trimmedInstitution } : {}),
    });
  }

  // Note: modal title uses "Log a …" / "Edit a …" to avoid a duplicate-match
  // clash with the submit button text — testing-library's getByText(regex)
  // throws when multiple elements share the same match.
  const title = isEditing
    ? mode === 'contribution' ? 'Edit a contribution' : 'Edit a withdrawal'
    : mode === 'contribution' ? 'Log a contribution' : 'Log a withdrawal';
  const submitLabel = isEditing ? 'Save changes' : `Log ${mode}`;

  return (
    <Modal open title={title} onClose={onClose}>
      <div class="space-y-4">
        <label class="block" htmlFor="log-amount">
          <span class="text-sm font-medium text-body">Amount</span>
          <div class="relative mt-1">
            <span aria-hidden="true" class="absolute left-3 top-1/2 -translate-y-1/2 text-body/70 font-medium">
              $
            </span>
            <input
              id="log-amount"
              type="number"
              min={0}
              step={0.01}
              value={amount}
              aria-label="Amount in dollars"
              onInput={(e) =>
                setAmount(clampNonNegative((e.target as HTMLInputElement).value))
              }
              class="block w-full pl-8 pr-3 py-2.5 border border-card-border rounded-lg text-lg font-bold focus:outline-none focus:ring-2 focus:ring-hudson-navy"
              placeholder="0.00"
            />
          </div>
        </label>

        <label class="block" htmlFor="log-date">
          <span class="text-sm font-medium text-body">Date</span>
          <input
            id="log-date"
            type="date"
            value={date}
            max={todayDate}
            aria-label="Transaction date"
            onChange={(e) => {
              setDate((e.target as HTMLInputElement).value);
              // Clear stale error as user picks a new date.
              if (dateError) setDateError(null);
            }}
            class="mt-1 block w-full px-3 py-2.5 border border-card-border rounded-lg focus:outline-none focus:ring-2 focus:ring-hudson-navy"
          />
        </label>

        {/* P2-5: surfaced when submit handler rejects a future-dated entry */}
        {dateError && (
          <div
            role="alert"
            class="flex items-start gap-2 rounded-lg px-3 py-2 text-sm border bg-alert-red/10 border-alert-red text-alert-red font-medium"
          >
            <span aria-hidden="true" class="shrink-0 mt-0.5 font-bold">⚠</span>
            <span>{dateError}</span>
          </div>
        )}

        <label class="block" htmlFor="log-institution">
          <span class="text-sm font-medium text-body">Institution (optional)</span>
          <input
            id="log-institution"
            type="text"
            value={institution}
            aria-label="Institution name optional"
            onInput={(e) => setInstitution((e.target as HTMLInputElement).value)}
            class="mt-1 block w-full px-3 py-2.5 border border-card-border rounded-lg focus:outline-none focus:ring-2 focus:ring-hudson-navy"
            placeholder="e.g. Wealthsimple"
          />
        </label>

        {/* P0-2: Historical entry toggle. Only shown for contributions, and
            never in edit mode (the entry's posture was set when it was first
            logged; subsequent edits to amount/date are corrections, not
            reclassifications). */}
        {mode === 'contribution' && !isEditing && (
          <label class="flex items-start gap-2 text-sm text-body" htmlFor="log-historical">
            <input
              id="log-historical"
              type="checkbox"
              checked={isHistorical}
              onChange={(e) => setIsHistorical((e.target as HTMLInputElement).checked)}
              class="mt-0.5"
            />
            <span>This is a past contribution I&apos;m recording</span>
          </label>
        )}

        {/* Over-contribution warning — color + icon + text, never color alone (Lock 1 / C.13) */}
        {showWarning && (
          <div
            role="alert"
            class={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm border ${
              overCheck.severity === 'error'
                ? 'bg-alert-red/10 border-alert-red text-alert-red font-medium'
                : 'bg-grain-gold/15 border-grain-gold text-body/80'
            }`}
          >
            <span aria-hidden="true" class="shrink-0 mt-0.5 font-bold">⚠</span>
            <span>
              {overCheck.severity === 'error'
                ? `This would put you $${(parseInt(overCheck.by_cents) / 100).toFixed(2)} over your limit. CRA charges 1% per month on the excess. Reduce the amount.${recontribSentence}`
                : isHistorical
                  ? `This entry puts you over your 2026 TFSA limit by $${(parseInt(overCheck.by_cents) / 100).toLocaleString('en-CA')}. CRA charges 1% per month on the excess. Logging anyway will record this for your history.${recontribSentence}`
                  : `Warning: you'd be within $${(parseInt(overCheck.by_cents) / 100).toFixed(2)} of your limit — confirm your CRA room is accurate.${recontribSentence}`}
            </span>
          </div>
        )}

        <div class="flex gap-3 pt-2">
          <ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
          <ButtonPrimary
            onClick={handleSubmit}
            disabled={isBlockedByOverContrib}
          >
            {submitLabel}
          </ButtonPrimary>
        </div>
      </div>
    </Modal>
  );
}
